import { z } from "zod";
import {
  scopedId,
  capabilityIds,
  principalSchema,
  AccessError,
  type AccessPrincipal,
  type CapabilityId,
} from "./contracts";
export const capabilityClausesSchema = z
  .array(
    z
      .array(z.enum(capabilityIds))
      .min(1)
      .refine((v) => new Set(v).size === v.length),
  )
  .max(15);
export type CapabilityClauses = z.infer<typeof capabilityClausesSchema>;
export const operationSpecSchema = z
  .object({
    id: scopedId,
    method: z.enum(["GET", "POST", "PUT", "DELETE"]),
    path: z.string().startsWith("/api/"),
    required: capabilityClausesSchema,
    limit: z.enum(["mutation", "artifact"]).optional(),
    approvalRequiresRoles: z.boolean().optional(),
    probationRequiresOperations: z.boolean().optional(),
  })
  .strict();
export type OperationSpec = z.infer<typeof operationSpecSchema>;
export const denialReasons = [
  "CAPABILITY_MISSING",
  "SNAPSHOT_INVALID",
  "TENANT_MISMATCH",
  "SUBJECT_MISMATCH",
  "AUTHORIZATION_REVISION_STALE",
] as const;
export type DenialReason = (typeof denialReasons)[number];
export function operationDecision(
  principal: AccessPrincipal,
  required: CapabilityClauses,
  expected: {
    tenantId: string;
    subjectId: string;
    authorizationRevision: number;
  },
): { allowed: true } | { allowed: false; reason: DenialReason } {
  const parsed = principalSchema.safeParse(principal);
  if (!parsed.success || !parsed.data.authorization)
    return { allowed: false, reason: "SNAPSHOT_INVALID" };
  const p = parsed.data;
  if (p.tenantId !== expected.tenantId)
    return { allowed: false, reason: "TENANT_MISMATCH" };
  if (p.subject !== expected.subjectId)
    return { allowed: false, reason: "SUBJECT_MISMATCH" };
  if (p.revision !== expected.authorizationRevision)
    return { allowed: false, reason: "AUTHORIZATION_REVISION_STALE" };
  capabilityClausesSchema.parse(required);
  return required.every((group) =>
    group.some((c) => p.authorization!.capabilities.includes(c)),
  )
    ? { allowed: true }
    : { allowed: false, reason: "CAPABILITY_MISSING" };
}
export const denialRecordSchema = z
  .object({
    version: z.literal(1),
    id: scopedId,
    at: z.string().datetime(),
    tenantId: scopedId,
    actorId: scopedId,
    effectiveSubjectId: scopedId,
    operation: scopedId,
    required: capabilityClausesSchema,
    reason: z.enum(denialReasons),
  })
  .strict();
export type DenialRecord = z.infer<typeof denialRecordSchema>;
export const denialPageSchema = z
  .object({
    records: z.array(denialRecordSchema).max(100),
    nextCursor: scopedId.optional(),
  })
  .strict();
export type DenialPage = z.infer<typeof denialPageSchema>;
export interface DenialStore {
  append(record: DenialRecord): Promise<void>;
  list(tenantId: string, before?: string, limit?: number): Promise<DenialPage>;
}
export class MemoryDenialStore implements DenialStore {
  protected records: DenialRecord[];
  private writes: Promise<void> = Promise.resolve();
  constructor(initial: unknown = []) {
    this.records = z.array(denialRecordSchema).parse(initial);
    if (new Set(this.records.map((r) => r.id)).size !== this.records.length)
      throw new Error("Invalid denial audit identities");
  }
  protected async persistAppend(_record: DenialRecord) {}
  async append(raw: DenialRecord) {
    const record = denialRecordSchema.parse(raw);
    const operation = this.writes.then(async () => {
      if (this.records.some((r) => r.id === record.id))
        throw new Error("Duplicate denial audit identity");
      await this.persistAppend(structuredClone(record));
      this.records.push(record);
    });
    this.writes = operation.catch(() => {});
    await operation;
  }
  async list(
    tenantId: string,
    before?: string,
    limit = 50,
  ): Promise<DenialPage> {
    scopedId.parse(tenantId);
    if (!Number.isInteger(limit) || limit < 1 || limit > 100)
      throw new AccessError("AUDIT_PAGE_INVALID", 400);
    await this.writes;
    const own = this.records.filter((r) => r.tenantId === tenantId).reverse(),
      position = before ? own.findIndex((r) => r.id === before) + 1 : 0;
    if (before && position === 0)
      throw new AccessError("AUDIT_CURSOR_INVALID", 400);
    const records = own.slice(position, position + limit);
    return structuredClone({
      records,
      ...(position + limit < own.length
        ? { nextCursor: records.at(-1)!.id }
        : {}),
    });
  }
}
export const rateDecisionSchema = z
  .object({
    allowed: z.boolean(),
    retryAfterMs: z.number().finite().min(0).max(60000),
  })
  .strict()
  .refine((v) => (v.allowed ? v.retryAfterMs === 0 : v.retryAfterMs >= 1));
export type RateDecision = z.infer<typeof rateDecisionSchema>;
export interface RateLimiter {
  readonly shared?: boolean;
  consume(key: string, limit: number, windowMs: number): Promise<RateDecision>;
}
/** Sliding windows retain active counters at capacity; they are never evicted to admit another key. */
export class MemoryRateLimiter implements RateLimiter {
  private entries = new Map<string, { times: number[]; windowMs: number }>();
  private lastNow = 0;
  constructor(
    private readonly now: () => number = Date.now,
    private readonly maxKeys = 10000,
  ) {
    if (!Number.isInteger(maxKeys) || maxKeys < 1 || maxKeys > 10000)
      throw new Error("Invalid rate limiter capacity");
  }
  async consume(
    key: string,
    limit: number,
    windowMs: number,
  ): Promise<RateDecision> {
    if (
      !key ||
      key.length > 500 ||
      !Number.isInteger(limit) ||
      limit < 1 ||
      limit > 1000 ||
      !Number.isInteger(windowMs) ||
      windowMs < 1 ||
      windowMs > 60000
    )
      throw new Error("Invalid rate window");
    const reading = this.now();
    if (!Number.isFinite(reading)) throw new Error("Invalid limiter clock");
    const now = (this.lastNow = Math.max(reading, this.lastNow)),
      cutoff = now - windowMs;
    let entry = this.entries.get(key);
    if (entry && entry.windowMs !== windowMs)
      throw new Error("Rate key window changed");
    if (!entry) {
      for (const [id, value] of this.entries)
        if (value.times.at(-1)! <= now - value.windowMs)
          this.entries.delete(id);
      if (this.entries.size >= this.maxKeys)
        return {
          allowed: false,
          retryAfterMs: Math.max(
            1,
            Math.min(
              ...[...this.entries.values()].map(
                (v) => v.times.at(-1)! + v.windowMs - now,
              ),
            ),
          ),
        };
      entry = { times: [], windowMs };
      this.entries.set(key, entry);
    }
    entry.times = entry.times.filter((t) => t > cutoff);
    if (entry.times.length >= limit)
      return {
        allowed: false,
        retryAfterMs: Math.max(1, entry.times[0] + windowMs - now),
      };
    entry.times.push(now);
    return { allowed: true, retryAfterMs: 0 };
  }
}
